import React, { useContext, useRef } from 'react';
import { isNil, mapValues, merge, omitBy, pick } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { useRecoilValue, useRecoilRefresher_UNSTABLE } from 'recoil';
import { patchPrintSettings } from 'venn-api';
import { useMutation } from '@tanstack/react-query';
import { Button, getAppTitle, Loading, Notifications, NotificationType, Warning } from 'venn-ui-kit';
import { apiPrintSettings, DEFAULT_FONT_SIZE_OVERRIDES } from 'venn-state';
import { analyticsService, toClassName, withSuspense } from 'venn-utils';
import { FlexWrapper, Name, Section, StyledIcon as HeaderIcon, WrapperForSections } from './shared';
import { RequiredLabel, UserContext, FontSizeInput } from 'venn-components';
import { type FontOverridesFormData, fontOverridesFormSections } from './logic/fontSizeDefaultsLogic';
import { FontSizeConfirmationDialog } from './FontSizeConfirmationDialog';

const dataFields = [
  'legalFontOverrides',
  'blockFontOverrides',
  'sectionPageFontOverrides',
  'titlePageFontOverrides',
] as const;

export const ReportLabFontSizeDefaultsSection = withSuspense(<Loading title="Loading print settings..." />, () => {
  const { hasPermission } = useContext(UserContext);
  const isReadOnly = !hasPermission('MANAGE_PRINT_CONFIGURATION');
  const apiPrintSettingsData = useRecoilValue(apiPrintSettings);
  const resetRecoilPrintSettings = useRecoilRefresher_UNSTABLE(apiPrintSettings);
  const patchFontOverrides = useMutation((data: FontOverridesFormData) => patchPrintSettings(data), {
    onSuccess: () => {
      resetRecoilPrintSettings();
      Notifications.notify('Font size defaults have been updated successfully.');
    },
    onError: () => Notifications.notify('Failed to update font size defaults.', NotificationType.ERROR),
  });
  const dialogRef = useRef<HTMLDialogElement>(null);

  const { formState, handleSubmit, getValues, control, reset } = useForm<FontOverridesFormData>({
    mode: 'onSubmit',
    defaultValues: DEFAULT_FONT_SIZE_OVERRIDES,
    values: merge(
      {},
      DEFAULT_FONT_SIZE_OVERRIDES,
      mapValues(pick(apiPrintSettingsData, ...dataFields), (fontSizeSection) =>
        omitBy(fontSizeSection, (fontSize) => isNil(fontSize)),
      ) as FontOverridesFormData,
    ),
  });

  const onSubmit = () => {
    dialogRef.current?.showModal?.();
  };
  return patchFontOverrides.isLoading ? (
    <Loading title="Loading print settings..." />
  ) : (
    <WrapperForSections>
      <dialog ref={dialogRef}>
        <FontSizeConfirmationDialog
          onPrimaryClick={() => {
            const data = getValues();
            patchFontOverrides.mutate(data);
            analyticsService.ctaClicked({ purpose: 'saveWorkspaceDefaults' });
            reset(data);
          }}
          onClose={() => dialogRef.current?.close?.()}
          formState={formState}
        />
      </dialog>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="qa-report-lab-fonts">
        <Section>
          <div className="flex justify-between items-center">
            <FlexWrapper>
              <HeaderIcon type="text-size" />
              <Name>Report Lab Font Size Defaults</Name>
            </FlexWrapper>
            <Button disabled={!formState.isDirty || isReadOnly} icon="save" type="submit">
              Save changes
            </Button>
          </div>
        </Section>
        {fontOverridesFormSections.map((section) => (
          <Section key={section.name} data-testid={toClassName(section.name)}>
            <div className="flex flex-col gap-[20px]">
              <div className="flex flex-row justify-between">
                <div>
                  <div className="font-bold text-[16px]">{section.name}</div>
                </div>
                {section.affects === 'existing' && (
                  <Warning iconPrefix="fas" icon="triangle-exclamation" text="Will update all existing reports" />
                )}
                {section.affects === 'new' && (
                  <span className="text-venn-grey-90 text-[10px]">Only affects newly-created blocks</span>
                )}
              </div>

              <div className="grid grid-cols-4 gap-[6px]">
                {section.fields.map((field) => (
                  <div className="flex flex-col justify-between gap-[4px]" key={field.path}>
                    <RequiredLabel>{field.name}</RequiredLabel>
                    <div className="text-[12px]">
                      {field.description && (
                        <>
                          <span>{field.description}</span>
                          &nbsp;
                        </>
                      )}
                      <span className="text-venn-grey-80 whitespace-nowrap">
                        ({field.min}-{field.max}pt)
                      </span>
                    </div>
                    <span className="inline-flex gap-[4px] items-baseline">
                      <Controller
                        control={control}
                        name={field.path}
                        render={({ field: { onChange, value } }) => (
                          <FontSizeInput
                            name={field.name}
                            min={field.min}
                            max={field.max}
                            onChange={onChange}
                            value={value}
                            isReadOnly={isReadOnly}
                            onReset={() => onChange(field.default)}
                            resetEnabled={field.default !== getValues(field.path)}
                            resetTooltip={`Revert to ${getAppTitle()} default (${field.default}pt)`}
                          />
                        )}
                      />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        ))}
      </form>
    </WrapperForSections>
  );
});
